import { useEffect, useRef, useState, useCallback } from 'react';

const TOKEN_KEYS = ['nexus_access_token', 'nexus_token', 'access_token'];

function getAccessToken(): string | null {
  for (const key of TOKEN_KEYS) {
    const token = localStorage.getItem(key);
    if (token) return token;
  }
  return null;
}

export interface SSENotification {
  id: string;
  title: string;
  message: string;
  category?: string;
  action_url?: string;
  icon?: string;
  data?: Record<string, unknown>;
  created_at: string;
}

interface UseNotificationSSEOptions {
  onNotification?: (notification: SSENotification) => void;
  enabled?: boolean;
}

interface UseNotificationSSEReturn {
  connected: boolean;
  reconnecting: boolean;
  error: string | null;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000;

export function useNotificationSSE({
  onNotification,
  enabled = true,
}: UseNotificationSSEOptions = {}): UseNotificationSSEReturn {
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNotificationRef = useRef(onNotification);

  // Keep callback ref fresh
  onNotificationRef.current = onNotification;

  const cleanup = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setConnected(false);
    setReconnecting(false);
  }, []);

  const connect = useCallback(() => {
    const token = getAccessToken();
    if (!token) {
      setError('No auth token');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Use the configured API URL (same as axios client) — relative URLs don't work
    // in production since frontend (S3/CloudFront) and API are on different domains.
    const apiBase = import.meta.env.VITE_API_URL || '/api/v1';
    const url = `${apiBase}/notifications/stream?token=${encodeURIComponent(token)}`;
    console.log('[SSE] Connecting to', url);
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('connected', () => {
      console.log('[SSE] Connected — real-time notifications active');
      setConnected(true);
      setReconnecting(false);
      setError(null);
      reconnectAttempts.current = 0;
    });

    es.addEventListener('notification', (event) => {
      try {
        const data: SSENotification = JSON.parse(event.data);
        console.log('[SSE] Received notification:', data.title);
        onNotificationRef.current?.(data);
      } catch {
        console.error('[SSE] Failed to parse notification:', event.data);
      }
    });

    es.onerror = (err) => {
      console.warn('[SSE] Connection error, reconnecting...', err);
      es.close();
      eventSourceRef.current = null;
      setConnected(false);

      if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        setReconnecting(true);
        const delay = BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current);
        reconnectAttempts.current += 1;

        reconnectTimer.current = setTimeout(() => {
          connect();
        }, delay);
      } else {
        setReconnecting(false);
        setError('SSE connection failed after max retries. Falling back to polling.');
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      cleanup();
      return;
    }

    connect();
    return cleanup;
  }, [enabled, connect, cleanup]);

  // Reconnect when token changes (on refresh)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && TOKEN_KEYS.includes(e.key)) {
        cleanup();
        reconnectAttempts.current = 0;
        connect();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, cleanup]);

  return { connected, reconnecting, error };
}

export default useNotificationSSE;
